import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { FiMail, FiClock, FiActivity, FiSearch, FiMonitor, FiMapPin, FiCalendar } from "react-icons/fi";
import AdminLayout from "../../layouts/AdminLayout";
import apiClient from "../../api/apiClient";
import { getSentEmails, getSentEmailDetail } from "../../api/accountsService";
import useAuthStore from "../../store/authStore";

function MailTracking() {
  const isSuperUser = useAuthStore((state) => state.isSuperuser);
  const [emails, setEmails] = useState([]);
  const [selectedEmail, setSelectedEmail] = useState(null);
  const [selectedEmailDetails, setSelectedEmailDetails] = useState(null);
  const [loadingList, setLoadingList] = useState(true);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [errorMsg, setErrorMsg] = useState("");

  // Re-verify super admin role on mount via profile API
  useEffect(() => {
    const verifyRole = async () => {
      try {
        const id = useAuthStore.getState().employeeId;
        if (!id) return;
        const res = await apiClient.get(`/accounts/api/users/${id}/`);
        const user = res.data.user;
        const isSuper = user?.is_superuser === true;
        useAuthStore.getState().setAuth({ isSuperuser: isSuper });
      } catch (err) {
        console.error("Role verification failed", err);
      }
    };
    verifyRole();
  }, []);

  // Fetch emails list
  useEffect(() => {
    if (!isSuperUser) return;
    const fetchEmails = async () => {
      try {
        setLoadingList(true);
        const res = await getSentEmails();
        setEmails(res.data.emails || []);
      } catch (err) {
        console.error(err);
        setErrorMsg("Failed to load email tracking list.");
      } finally {
        setLoadingList(false);
      }
    };
    fetchEmails();
  }, [isSuperUser]);

  // Auto-polling for real-time dynamic updates of email dispatch list and recipient details
  useEffect(() => {
    if (!isSuperUser) return;

    const pollData = async () => {
      try {
        const listRes = await getSentEmails();
        setEmails(listRes.data.emails || []);

        if (selectedEmail) {
          const detailRes = await getSentEmailDetail(selectedEmail.id);
          setSelectedEmailDetails(detailRes.data.email || null);
        }
      } catch (err) {
        console.error("Dynamic update poll failed:", err);
      }
    };

    const intervalId = setInterval(pollData, 5000);

    return () => {
      clearInterval(intervalId);
    };
  }, [isSuperUser, selectedEmail]);

  // Fetch specific email details
  const handleSelectEmail = async (email) => {
    setSelectedEmail(email);
    try {
      setLoadingDetails(true);
      setErrorMsg("");
      const res = await getSentEmailDetail(email.id);
      setSelectedEmailDetails(res.data.email || null);
    } catch (err) {
      console.error(err);
      setErrorMsg("Failed to load tracking details for selected email.");
    } finally {
      setLoadingDetails(false);
    }
  };

  // Format date string
  const formatDate = (dateString) => {
    if (!dateString) return "—";
    const dateObj = new Date(dateString);
    return dateObj.toLocaleDateString("en-US", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  };

  const filteredEmails = emails.filter((email) => {
    const term = searchQuery.toLowerCase();
    return (
      email.subject?.toLowerCase().includes(term) ||
      email.id.toString().includes(term)
    );
  });

  // Access Denied layout if not super admin
  if (!isSuperUser) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center min-h-[70vh] p-4">
          <div className="bg-white/10 backdrop-blur-xl rounded-3xl border border-white/20 p-8 text-center max-w-md shadow-2xl text-white">
            <div className="w-16 h-16 bg-red-500/20 text-red-400 rounded-2xl flex items-center justify-center mx-auto mb-5 text-3xl border border-red-500/30">
              ⚠️
            </div>
            <h1 className="text-2xl font-extrabold tracking-tight">Access Denied</h1>
            <p className="text-white/70 text-sm mt-3 leading-relaxed">
              Only Super Administrators are permitted to access the email tracking statistics dashboard.
            </p>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="p-2 sm:p-6 text-white">
        <div className="w-full max-w-[1280px] mx-auto space-y-6">
          {/* Header Banner */}
          <div className="bg-white/10 backdrop-blur-xl rounded-3xl border border-white/20 shadow-2xl overflow-hidden">
            <div className="bg-gradient-to-r from-violet-600/50 to-indigo-600/50 px-6 py-8 border-b border-white/15 flex items-center justify-between gap-4">
              <div className="flex items-center gap-5 sm:gap-6">
                <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-2xl bg-white/10 flex items-center justify-center shadow-lg border border-white/20 backdrop-blur-md flex-shrink-0">
                  <FiActivity className="text-3xl text-violet-200" />
                </div>
                <div>
                  <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight">Mail Tracking Dashboard</h1>
                  <p className="text-violet-200/80 text-xs sm:text-sm mt-1">
                    Monitor email delivery rates and see who opened/read custom system notifications.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {errorMsg && (
            <div className="bg-red-500/20 border border-red-400/40 rounded-2xl px-5 py-4 flex items-start gap-3 text-left">
              <div className="text-red-400 mt-0.5 font-bold">⚠️</div>
              <div>
                <p className="text-red-300 text-sm font-semibold">Error</p>
                <p className="text-red-200/90 text-xs sm:text-sm mt-0.5">{errorMsg}</p>
              </div>
            </div>
          )}

          {/* Main Dashboard Layout */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
            {/* LEFT COLUMN: Email History List */}
            <div className="lg:col-span-5 space-y-4">
              <div className="bg-white/5 border border-white/15 rounded-3xl p-5 backdrop-blur-lg">
                <h2 className="text-lg font-bold mb-4 flex items-center gap-2">
                  <FiMail className="text-violet-300" /> Email Dispatch History
                </h2>

                {/* Search Bar */}
                <div className="relative mb-4">
                  <FiSearch className="absolute left-3.5 top-3.5 text-white/40" />
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search by subject..."
                    className="w-full bg-white/5 border border-white/15 rounded-2xl pl-10 pr-4 py-2.5 text-white text-sm outline-none focus:border-violet-400 focus:bg-white/10 transition-all placeholder-white/35"
                  />
                </div>

                {/* List Container */}
                <div className="space-y-3 max-h-[600px] overflow-y-auto pr-1 scrollbar-thin scrollbar-thumb-white/20 scrollbar-track-transparent">
                  {loadingList ? (
                    <div className="flex flex-col items-center justify-center py-10 gap-3">
                      <div className="w-8 h-8 border-3 border-violet-400 border-t-transparent rounded-full animate-spin"></div>
                      <span className="text-xs text-white/50">Loading dispatch records...</span>
                    </div>
                  ) : filteredEmails.length === 0 ? (
                    <div className="py-12 text-center text-white/40 text-sm bg-white/5 border border-white/5 rounded-2xl">
                      No matching emails found.
                    </div>
                  ) : (
                    filteredEmails.map((email) => {
                      const isSelected = selectedEmail?.id === email.id;
                      return (
                        <motion.div
                          key={email.id}
                          whileHover={{ scale: 1.01 }}
                          whileTap={{ scale: 0.99 }}
                          onClick={() => handleSelectEmail(email)}
                          className={`p-4 rounded-2xl border text-left cursor-pointer transition-all ${
                            isSelected
                              ? "bg-violet-600/30 border-violet-400 shadow-lg"
                              : "bg-white/5 border-white/10 hover:bg-white/10 hover:border-white/20"
                          }`}
                        >
                          <div className="flex justify-between items-start gap-2">
                            <h3 className="font-bold text-sm sm:text-base line-clamp-1 flex-1">
                              {email.subject}
                            </h3>
                            <span className="text-[10px] sm:text-xs font-semibold px-2 py-0.5 rounded-full bg-white/10 text-white/80 shrink-0">
                              #{email.id}
                            </span>
                          </div>

                          <div className="flex items-center gap-2 mt-1.5 text-[11px] text-white/60">
                            <FiCalendar className="w-3.5 h-3.5" />
                            <span>{formatDate(email.sent_at)}</span>
                          </div>

                          {/* Stats Summary Bar */}
                          <div className="mt-3 space-y-1.5">
                            <div className="flex justify-between text-xs">
                              <span className="text-white/65">
                                {email.opened_count} / {email.total_recipients} Opened
                              </span>
                              <span className="font-bold text-violet-300">{email.open_rate}%</span>
                            </div>
                            <div className="w-full h-2 bg-white/10 rounded-full overflow-hidden">
                              <div
                                className="h-full bg-gradient-to-r from-violet-500 to-indigo-500 rounded-full transition-all duration-500"
                                style={{ width: `${email.open_rate}%` }}
                              ></div>
                            </div>
                          </div>
                        </motion.div>
                      );
                    })
                  )}
                </div>
              </div>
            </div>

            {/* RIGHT COLUMN: Tracking analytics details */}
            <div className="lg:col-span-7">
              <div className="bg-white/5 border border-white/15 rounded-3xl p-5 min-h-[500px] backdrop-blur-lg text-left flex flex-col">
                {!selectedEmail ? (
                  <div className="flex-1 flex flex-col items-center justify-center py-20 text-center text-white/40">
                    <div className="w-16 h-16 bg-white/5 rounded-2xl flex items-center justify-center mb-4 text-2xl border border-white/10">
                      📊
                    </div>
                    <h3 className="text-lg font-bold text-white/80">No Email Selected</h3>
                    <p className="text-xs max-w-xs mt-1 leading-relaxed">
                      Select a sent email from the left sidebar history to review detailed recipient status.
                    </p>
                  </div>
                ) : loadingDetails ? (
                  <div className="flex-1 flex flex-col items-center justify-center py-20 gap-3">
                    <div className="w-10 h-10 border-4 border-violet-400 border-t-transparent rounded-full animate-spin"></div>
                    <span className="text-sm text-white/50">Fetching tracking details...</span>
                  </div>
                ) : !selectedEmailDetails ? (
                  <div className="text-center py-12 text-white/40">
                    Could not fetch details. Please try again.
                  </div>
                ) : (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="space-y-6 flex-1 flex flex-col"
                  >
                    {/* Header Details */}
                    <div className="space-y-3 pb-5 border-b border-white/10">
                      <div className="flex flex-wrap justify-between items-center gap-3">
                        <span className="text-[10px] uppercase font-bold tracking-wider px-3 py-1 bg-violet-600/30 border border-violet-400/40 rounded-full">
                          Email Info
                        </span>
                        <span className="text-xs text-white/55">
                          Sent: {formatDate(selectedEmailDetails.sent_at)}
                        </span>
                      </div>

                      <h2 className="text-xl sm:text-2xl font-extrabold tracking-tight">
                        {selectedEmailDetails.subject}
                      </h2>

                      <div className="text-xs text-white/60">
                        <span>Sender: </span>
                        <strong className="text-white">{selectedEmailDetails.sender_name}</strong>
                      </div>
                    </div>

                    {/* Email Body Preview Accordion */}
                    <div className="bg-white/5 border border-white/10 rounded-2xl overflow-hidden">
                      <div className="px-4 py-3 bg-white/5 border-b border-white/10 text-xs font-bold text-white/70 uppercase tracking-wider flex items-center gap-1.5">
                        📝 Email Body Preview
                      </div>
                      <div className="p-4 max-h-[160px] overflow-y-auto scrollbar-thin scrollbar-thumb-white/20 scrollbar-track-transparent text-sm text-white/80 leading-relaxed font-mono whitespace-pre-wrap">
                        {selectedEmailDetails.body}
                      </div>
                    </div>

                    {/* Tracking Summary Stats Grid */}
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-4">
                      <div className="bg-white/5 border border-white/10 rounded-2xl p-4 text-center">
                        <p className="text-[10px] text-white/50 uppercase font-semibold">Total Recipients</p>
                        <p className="text-2xl font-extrabold mt-1">{selectedEmailDetails.recipients.length}</p>
                      </div>
                      <div className="bg-white/5 border border-white/10 rounded-2xl p-4 text-center">
                        <p className="text-[10px] text-emerald-400 uppercase font-semibold">Total Opened</p>
                        <p className="text-2xl font-extrabold text-emerald-400 mt-1">
                          {selectedEmailDetails.recipients.filter(r => r.is_opened).length}
                        </p>
                      </div>
                      <div className="bg-white/5 border border-white/10 rounded-2xl p-4 text-center">
                        <p className="text-[10px] text-white/50 uppercase font-semibold">Unopened</p>
                        <p className="text-2xl font-extrabold text-white/70 mt-1">
                          {selectedEmailDetails.recipients.filter(r => r.is_sent && !r.is_opened).length}
                        </p>
                      </div>
                      <div className="bg-white/5 border border-white/10 rounded-2xl p-4 text-center">
                        <p className="text-[10px] text-red-400 uppercase font-semibold">Not Received</p>
                        <p className="text-2xl font-extrabold text-red-400 mt-1">
                          {selectedEmailDetails.recipients.filter(r => !r.is_sent).length}
                        </p>
                      </div>
                      <div className="bg-white/5 border border-white/10 rounded-2xl p-4 text-center">
                        <p className="text-[10px] text-violet-300 uppercase font-semibold">Open Rate</p>
                        <p className="text-2xl font-extrabold text-violet-300 mt-1">
                          {selectedEmailDetails.recipients.filter(r => r.is_sent).length > 0
                            ? Math.round(
                                (selectedEmailDetails.recipients.filter(r => r.is_opened).length /
                                  selectedEmailDetails.recipients.filter(r => r.is_sent).length) *
                                  100
                              )
                            : 0}
                          %
                        </p>
                      </div>
                    </div>

                    {/* RECIPIENTS OPEN TRACKING */}
                    <div className="space-y-3 flex-1 flex flex-col">
                      <h3 className="text-sm font-bold text-white/70 uppercase tracking-wider">
                        Recipient List Status
                      </h3>

                      {/* Recipient Rows Container */}
                      <div className="space-y-2 max-h-[350px] overflow-y-auto pr-1 scrollbar-thin scrollbar-thumb-white/25 scrollbar-track-transparent flex-1">
                        {selectedEmailDetails.recipients.map((recipient, index) => {
                          const hasPhoto = recipient.photo;
                          return (
                            <div
                              key={index}
                              className="group flex items-center justify-between p-3.5 bg-white/5 border border-white/10 rounded-2xl gap-4 hover:bg-white/10 hover:border-white/15 transition-all relative"
                            >
                              {/* Left Info: Avatar + Details */}
                              <div className="flex items-center gap-3 min-w-0">
                                {hasPhoto ? (
                                  <img
                                    src={recipient.photo.startsWith("http") ? recipient.photo : recipient.photo}
                                    alt={recipient.full_name}
                                    className="w-9 h-9 rounded-full object-cover border border-white/20"
                                    onError={(e) => {
                                      e.target.style.display = "none";
                                      e.target.nextSibling.style.display = "flex";
                                    }}
                                  />
                                ) : null}
                                <div
                                  className="w-9 h-9 rounded-full bg-violet-500/20 border border-white/10 items-center justify-center text-xs text-violet-300 font-semibold"
                                  style={{ display: hasPhoto ? "none" : "flex" }}
                                >
                                  {recipient.full_name?.split(" ").map(n => n[0]).join("").slice(0, 2).toUpperCase()}
                                </div>
                                <div className="min-w-0 text-left">
                                  <p className="text-sm font-bold truncate text-white">
                                    {recipient.full_name}
                                  </p>
                                  <p className="text-xs text-white/50 truncate">
                                    {recipient.email}
                                  </p>
                                </div>
                              </div>

                              {/* Right Info: Status Icon & Tooltip */}
                              <div className="flex items-center gap-3 shrink-0">
                                {recipient.is_opened ? (
                                  <div className="flex items-center gap-2">
                                    <span className="hidden sm:inline text-xs text-emerald-400 font-medium">
                                      Opened
                                    </span>
                                    {/* Green Tick Circle */}
                                    <div className="w-5 h-5 rounded-full bg-emerald-500 text-white flex items-center justify-center shadow-lg border border-emerald-400/50">
                                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="3" viewBox="0 0 24 24">
                                        <polyline points="20 6 9 17 4 12" />
                                      </svg>
                                    </div>
                                  </div>
                                ) : !recipient.is_sent ? (
                                  <div className="flex items-center gap-2">
                                    <span className="hidden sm:inline text-xs text-red-400 font-medium">
                                      Not Received
                                    </span>
                                    {/* Red Cross Circle */}
                                    <div className="w-5 h-5 rounded-full bg-red-500 text-white flex items-center justify-center shadow-lg border border-red-400/50">
                                      <svg className="w-3 h-3" fill="none" stroke="currentColor" strokeWidth="3" viewBox="0 0 24 24">
                                        <line x1="18" y1="6" x2="6" y2="18"></line>
                                        <line x1="6" y1="6" x2="18" y2="18"></line>
                                      </svg>
                                    </div>
                                  </div>
                                ) : (
                                  <div className="flex items-center gap-2">
                                    <span className="hidden sm:inline text-xs text-white/40">
                                      Unopened
                                    </span>
                                    {/* Gray circle */}
                                    <div className="w-5 h-5 rounded-full border-2 border-white/20 bg-white/5"></div>
                                  </div>
                                )}

                                {/* Tooltip details on hover */}
                                {recipient.is_opened && (
                                  <div className="absolute right-12 top-1/2 -translate-y-1/2 bg-slate-900 border border-white/15 px-3 py-2 rounded-xl text-[10px] text-white/80 space-y-1 shadow-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none z-50 min-w-[180px]">
                                    <div className="flex items-center gap-1.5">
                                      <FiClock className="text-violet-400" />
                                      <span>{formatDate(recipient.opened_at)}</span>
                                    </div>
                                    {recipient.ip_address && (
                                      <div className="flex items-center gap-1.5">
                                        <FiMapPin className="text-cyan-400" />
                                        <span>IP: {recipient.ip_address}</span>
                                      </div>
                                    )}
                                    {recipient.user_agent && (
                                      <div className="flex items-center gap-1.5 truncate max-w-[200px]">
                                        <FiMonitor className="text-amber-400" />
                                        <span>{recipient.user_agent.split("(")[0]}</span>
                                      </div>
                                    )}
                                  </div>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </motion.div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}

export default MailTracking;
